import { Job, Issue } from "@/data/types";
import { getStageConfig } from "@/data/workflowEngine";
import { getJobBlockers } from "@/data/workflowBlockers";
import { getCanonicalInstallReadiness, getCanonicalReadinessScore } from "@/data/readiness";
import { useAppData } from "@/data/AppDataContext";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import { AlertTriangle, CheckCircle, XCircle, Clock, ClipboardCheck, ShieldAlert } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";

interface Props {
  job: Job;
  issues: Issue[];
  compact?: boolean;
}

function getReadinessChecks(job: Job, openIssues: Issue[]) {
  return [
    {
      label: "Permit",
      ok: !job.permitRequired || job.permitStatus === "Approved",
      status: !job.permitRequired ? "Not Required" : job.permitStatus,
      warn: job.permitRequired && (job.permitStatus === "Revision Needed" || job.permitStatus === "Submitted"),
      critical: job.permitRequired && job.permitStatus === "Revision Needed",
    },
    {
      label: "Order",
      ok: job.planningStatus === "Ordered" || job.planningStatus === "Awaiting Delivery" || job.planningStatus === "Receiving" || job.planningStatus === "Staged" || job.planningStatus === "Ready for Install",
      status: job.planningStatus || "Not Started",
      warn: !job.planningStatus || job.planningStatus === "In Planning" || job.planningStatus === "Ready for Ordering",
      critical: false,
    },
    {
      label: "Materials",
      ok: job.materialReadiness === "Fully Received" || job.materialReadiness === "Staged for Job" || job.materialReadiness === "Loaded for Crew",
      status: job.materialReadiness,
      warn: job.materialReadiness === "Missing Items" || job.materialReadiness === "Field Purchase Required",
      critical: job.materialReadiness === "Missing Items",
    },
    {
      label: "Dispatch",
      ok: job.dispatchStatus === "Assigned" || job.dispatchStatus === "Accepted",
      status: job.dispatchStatus,
      warn: job.dispatchStatus === "Rejected",
      critical: job.dispatchStatus === "Rejected",
    },
    {
      label: "Assignment",
      ok: job.assignmentType !== "Unassigned",
      status: job.assignmentType === "Unassigned" ? "Unassigned" : (job.assignedCrew || job.assignedSubcontractor || "Assigned"),
      warn: job.assignmentType === "Unassigned",
      critical: false,
    },
    {
      label: "Issues",
      ok: openIssues.length === 0,
      status: openIssues.length === 0 ? "Clear" : `${openIssues.length} Open`,
      warn: openIssues.length > 0,
      critical: openIssues.some((i) => i.severity === "Urgent"),
    },
    {
      label: "Install QA",
      ok: job.qaSubmitted,
      status: job.qaSubmitted ? "Submitted" : "Not Submitted",
      warn: !job.qaSubmitted && (getStageConfig(job.currentStage)?.order ?? 0) >= 80,
      critical: !job.qaSubmitted && (getStageConfig(job.currentStage)?.order ?? 0) >= 90,
    },
  ];
}

export function getReadinessScore(job: Job, _openIssues: Issue[], buildReviewEvents: BuildReviewEvent[]): number {
  // Delegate to canonical readiness engine
  return getCanonicalReadinessScore(job, buildReviewEvents);
}

/** Install readiness status for pre-install preparation */
export type InstallReadiness = "Ready for Install" | "Partially Ready" | "Not Ready";

export function getInstallReadiness(
  job: Job,
  openIssues: Issue[],
  buildReviewEvents: BuildReviewEvent[],
): {
  status: InstallReadiness;
  score: number;
  checks: { label: string; ok: boolean; critical: boolean }[];
  blockerCount: number;
  criticalCount: number;
} {
  // Delegate to canonical readiness engine — single source of truth
  return getCanonicalInstallReadiness(job, openIssues, buildReviewEvents);
}

export function JobWorkflowStatus({ job, issues, compact = false }: Props) {
  const openIssues = issues.filter((i) => i.status === "Open" || i.status === "In Progress");
  const checks = getReadinessChecks(job, openIssues);
  const blockers = getJobBlockers(job, openIssues);
  const score = Math.round((checks.filter((c) => c.ok).length / checks.length) * 100);
  const hasCritical = blockers.some((b) => b.severity === "critical");

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Progress value={score} className="h-1.5 w-16" />
        <span className={`text-[10px] font-bold ${score === 100 ? "text-success" : hasCritical ? "text-destructive" : "text-warning"}`}>
          {score}%
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Readiness Score */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em]">Install Readiness</span>
            <span className={`text-sm font-bold ${score === 100 ? "text-success" : hasCritical ? "text-destructive" : "text-warning"}`}>
              {score}%
            </span>
          </div>
          <Progress value={score} className={`h-2 ${score === 100 ? "[&>div]:bg-success" : hasCritical ? "[&>div]:bg-destructive" : "[&>div]:bg-warning"}`} />
        </div>
        {score === 100 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-success bg-success/10 border border-success/20 rounded-md px-2 py-1 shrink-0">
            <CheckCircle className="h-3 w-3" /> READY
          </span>
        )}
      </div>

      {/* Alert banners */}
      {hasCritical && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5">
          <ShieldAlert className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-destructive">
              {blockers.filter((b) => b.severity === "critical").length} Critical Blocker{blockers.filter((b) => b.severity === "critical").length > 1 ? "s" : ""}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              This job cannot proceed until critical blockers are resolved.
            </p>
          </div>
        </div>
      )}

      {!job.qaSubmitted && (getStageConfig(job.currentStage)?.order ?? 0) >= 90 && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2.5">
          <ClipboardCheck className="h-4 w-4 text-warning mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold" style={{ color: "hsl(var(--warning))" }}>Install QA Required</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              The Install QA form must be submitted before this job can be marked complete.{" "}
              <Link to={`/qa?job=${job.id}`} className="text-primary underline underline-offset-2 hover:text-primary/80">
                Submit QA Form →
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* Readiness Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
        {checks.map((c) => (
          <div
            key={c.label}
            className={`rounded-md border px-2.5 py-2 text-center ${
              c.ok
                ? "border-success/30 bg-success/5"
                : c.critical
                ? "border-destructive/30 bg-destructive/5"
                : c.warn
                ? "border-warning/30 bg-warning/5"
                : "border-border bg-muted/30"
            }`}
          >
            <div className="flex items-center justify-center gap-1 mb-0.5">
              {c.ok ? (
                <CheckCircle className="h-3 w-3 text-success" />
              ) : c.critical ? (
                <XCircle className="h-3 w-3 text-destructive" />
              ) : c.warn ? (
                <AlertTriangle className="h-3 w-3 text-warning" />
              ) : (
                <Clock className="h-3 w-3 text-muted-foreground" />
              )}
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {c.label}
              </span>
            </div>
            <p className={`text-xs font-medium truncate ${c.ok ? "text-success" : c.critical ? "text-destructive" : c.warn ? "text-warning" : "text-foreground"}`}>
              {c.status}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
